import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useHistory, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import AuditContextBar, { accountAvailability } from "../Components/AuditContextBar";
import AuditAccountCard from "../Components/AuditAccountCard";
import AuditAccountSwitcher from "../Components/AuditAccountSwitcher";
import AuditOverviewHeader from "../Components/AuditOverviewHeader";
import ControlsCategoriesView from "../Components/ControlsCategoriesView";
import ControlsHeader from "../Components/ControlsHeader";
import ControlResourcesDrawer from "../Components/ControlResourcesDrawer";
import ErrorUI from "../Components/ErrorUI";
import { AuditRefreshProvider } from "../Components/AuditRefreshContext";
import { ACCOUNT_DETAIL_FONT_SX, AH_WHITE, ComplianceFontProvider, auditHubPageSx } from "../AuditHub/theme";
import { buildComplianceControlsPath, scorePercent } from "../AuditHub/utils";
import { getAudit, unwrapGet } from "../../../helpers/auditHub_helper";
import { useAuditControlIndex } from "../Components/ControlAuditPanel";
import {
  getComplianceControls,
  getComplianceControlSections,
  clearAllComplianceMessages,
  clearComplianceControls,
} from "../../../store/compliance/complianceSlice";

// Assessment view fills the area between the fixed app header (56px) and the footer (60px):
// the context bar stays put and only the content below it scrolls.
const auditPageSx = {
  ...auditHubPageSx,
  padding: "12px",
  mb: "60px",
  backgroundColor: AH_WHITE,
  boxSizing: "border-box",
  height: "calc(100vh - 116px)",
  minHeight: 360,
  display: "flex",
  flexDirection: "column",
  gap: 2,
  overflow: "hidden",
  maxWidth: "100vw",
};

const auditScrollSx = {
  flex: "1 1 auto",
  minHeight: 0,
  overflowY: "auto",
  overflowX: "hidden",
  overscrollBehavior: "contain",
  "& > * + *": { mt: 2 },
};

const ScrollRegion = ({ enabled, children }) =>
  enabled ? <Box sx={auditScrollSx}>{children}</Box> : <>{children}</>;

const ControlsPage = () => {
  const [selectedSection, setSelectedSection] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedControlId, setSelectedControlId] = useState(null);
  const [selectedResourcesUri, setSelectedResourcesUri] = useState(null);
  const [selectedAvailableTypes, setSelectedAvailableTypes] = useState([]);
  const [selectedAvailableRegions, setSelectedAvailableRegions] = useState([]);
  const [selectedRunId, setSelectedRunId] = useState(null);
  // The assessment overview opens collapsed on every visit.
  const [overviewOpen, setOverviewOpen] = useState(false);
  const toggleOverview = useCallback(() => setOverviewOpen((open) => !open), []);

  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const searchParams = new URLSearchParams(location.search);
  const compliance = searchParams.get("compliance");
  const runId = searchParams.get("runId");
  const auditId = searchParams.get("auditId");
  const accountNumber = searchParams.get("accountNumber") || localStorage.getItem("selectedAccountNumber");
  const seededAudit = location.state?.auditDetail;
  const seededMatches = Boolean(seededAudit?.auditId) && seededAudit.auditId === auditId;
  const [auditState, setAuditState] = useState(() =>
    seededMatches ? { status: "ready", audit: seededAudit, error: null } : { status: "idle", audit: null, error: null }
  );
  const auditRequest = useRef(0);
  const seededAuditId = useRef(seededMatches ? auditId : null);

  // A silent load keeps the current numbers on screen and only replaces them with a successful response.
  const loadAudit = useCallback(
    (silent) => {
      if (!auditId) return;
      auditRequest.current += 1;
      const requestId = auditRequest.current;
      if (!silent) setAuditState({ status: "loading", audit: null, error: null });
      getAudit(auditId)
        .then((res) => {
          if (auditRequest.current === requestId) setAuditState({ status: "ready", audit: unwrapGet(res), error: null });
        })
        .catch((error) => {
          if (auditRequest.current !== requestId || silent) return;
          setAuditState({ status: "error", audit: null, error: error?.message || "Unable to load this assessment." });
        });
    },
    [auditId]
  );
  const refreshAudit = useCallback(() => loadAudit(true), [loadAudit]);

  useEffect(() => {
    if (!auditId) {
      auditRequest.current += 1;
      setAuditState({ status: "idle", audit: null, error: null });
      return undefined;
    }
    // Opening a framework already loaded this assessment. Reuse it; a direct visit still fetches once.
    const refreshIfVisible = () => {
      if (document.visibilityState === "visible") loadAudit(true);
    };
    document.addEventListener("visibilitychange", refreshIfVisible);
    if (seededAuditId.current === auditId) {
      return () => {
        document.removeEventListener("visibilitychange", refreshIfVisible);
      };
    }
    loadAudit(false);
    // Scans finish and other reviewers decide controls elsewhere, so the audit is re-read when the user returns.
    return () => {
      auditRequest.current += 1;
      document.removeEventListener("visibilitychange", refreshIfVisible);
    };
  }, [auditId, loadAudit]);

  const auditRoster = useAuditControlIndex(
    accountNumber,
    compliance,
    auditId || "",
    auditState.status === "ready" ? auditState.audit : null
  );

  const auditAccounts = Array.isArray(auditState.audit?.accounts) ? auditState.audit.accounts : [];
  const currentAuditAccount = auditAccounts.find((account) => account.accountNumber === accountNumber) || null;
  const auditScope = currentAuditAccount ? accountAvailability(currentAuditAccount, compliance).scope : null;
  const latestRunId = auditScope?.latestRunId || null;
  const latestRunEndedAt = auditScope?.latestRunEndedAt || null;

  // When a newer scan of this account finishes, move to it so every number on the page is from that run.
  useEffect(() => {
    if (!auditId || !compliance || !latestRunId || latestRunId === runId) return;
    if (latestRunEndedAt) localStorage.setItem("selectedScanDate", latestRunEndedAt);
    history.replace(
      buildComplianceControlsPath({ frameworkName: compliance, runId: latestRunId, accountNumber, auditId })
    );
  }, [auditId, compliance, latestRunId, latestRunEndedAt, runId, accountNumber, history]);

  const handleAccountChange = (nextAccountNumber) => {
    if (!nextAccountNumber || nextAccountNumber === accountNumber) return;
    const account = auditAccounts.find((item) => item.accountNumber === nextAccountNumber);
    const { scope } = account ? accountAvailability(account, compliance) : { scope: null };
    if (!scope) return;
    localStorage.setItem("selectedAccountNumber", nextAccountNumber);
    if (scope.latestRunEndedAt) localStorage.setItem("selectedScanDate", scope.latestRunEndedAt);
    history.replace(
      buildComplianceControlsPath({
        frameworkName: compliance,
        runId: scope.latestRunId || "",
        accountNumber: nextAccountNumber,
        auditId,
      })
    );
  };

  // Redux selectors
  const {
    complianceControlSections,
    complianceControlSectionsRunId,
    isGettingComplianceControlSections,
    getComplianceControlSectionsError,
    complianceControls,
    isGettingComplianceControls,
    getComplianceControlsError,
  } = useSelector((state) => state.compliance);

  const accountMetricsStatus =
    auditState.status === "ready" ? "ready" : auditState.status === "error" ? "error" : "loading";
  const accountSwitcher =
    auditId && compliance && auditAccounts.length > 1 ? (
      <AuditAccountSwitcher
        accounts={auditAccounts}
        compliance={compliance}
        accountNumber={accountNumber}
        onChange={handleAccountChange}
      />
    ) : null;
  const sectionsReady =
    !isGettingComplianceControlSections &&
    !getComplianceControlSectionsError &&
    Array.isArray(complianceControlSections?.controlsListJson) &&
    complianceControlSections.controlsListJson.length > 0;

  // Inside an audit the account's own scope is authoritative; local storage only holds the last dashboard click.
  const scanDate = auditScope?.latestRunEndedAt || localStorage.getItem("selectedScanDate");
  const compliancePercent =
    (auditScope?.hasScanResult ? scorePercent(auditScope.auditScore) : null) ??
    localStorage.getItem("selectedCompliancePercent");

  // Fetch sections on mount or param change
  useEffect(() => {
    dispatch(clearComplianceControls());
    dispatch(clearAllComplianceMessages());
    setSelectedSection(null);
    if (compliance && runId) {
      dispatch(getComplianceControlSections({ compliance, runId }));
    }
  }, [compliance, runId, dispatch]);

  // Fetch controls for selected section
  useEffect(() => {
    if (selectedSection) {
      dispatch(getComplianceControls({ compliance, runId, section: selectedSection }));
    }
  }, [selectedSection, compliance, runId, dispatch]);

  // Handler for section selection
  const handleSectionSelect = (section) => {
    console.log("🚀 ~ handleSectionSelect ~ section:", section)
    setSelectedSection(section);
  };

  // Handler for resource drawer
  const handleResourceClick = (controlId, resourcesUri, row) => {
    setSelectedControlId(controlId);
    setSelectedResourcesUri(resourcesUri);
    setSelectedAvailableTypes(row.AvailableTypes || []);
    setSelectedAvailableRegions(row.AvailableRegions || []);
    setSelectedRunId(runId);
    setDrawerOpen(true);
  };

  return (
    <ComplianceFontProvider>
    <AuditRefreshProvider value={refreshAudit}>
    <Box sx={auditId ? auditPageSx : {
      padding: "12px", 
      mb: "3rem", 
      backgroundColor: "#F9F9F9", 
      minHeight: "100vh", 
      display: "flex", 
      flexDirection: "column", 
      gap: 2,
      overflow: 'hidden', // Prevent horizontal scroll
      maxWidth: '100vw', // Ensure it doesn't exceed viewport width
      ...ACCOUNT_DETAIL_FONT_SX,
    }}>
      {auditId ? (
        <Box sx={{ flexShrink: 0 }}>
          <AuditContextBar auditId={auditId} state={auditState} />
        </Box>
      ) : null}
      <ScrollRegion enabled={Boolean(auditId)}>
      {auditId && compliance && auditAccounts.length > 1 ? (
        <AuditOverviewHeader
          audit={auditState.audit}
          compliance={compliance}
          accountNumber={accountNumber}
          onAccountChange={handleAccountChange}
          activeRunId={runId || ""}
          activeResources={
            !isGettingComplianceControlSections &&
            complianceControlSections &&
            complianceControlSectionsRunId &&
            complianceControlSectionsRunId === runId
              ? {
                  total: Number(complianceControlSections.totalResources) || 0,
                  passed: Number(complianceControlSections.passedResources) || 0,
                  failed: Number(complianceControlSections.failedResources) || 0,
                }
              : null
          }
        />
      ) : null}
      {/* The account card is not drawn without results, so the switcher stays reachable here. */}
      {accountSwitcher && runId && !isGettingComplianceControlSections && !sectionsReady ? (
        <Box sx={{ display: "flex", maxWidth: { xs: "100%", sm: 380 } }}>{accountSwitcher}</Box>
      ) : null}
      {auditId && compliance && !runId ? (
        <AuditAccountCard
          scope={auditScope}
          audit={auditState.audit}
          resources={null}
          compliance={compliance}
          scanDate={null}
          accountNumber={accountNumber}
          accountSwitcher={accountSwitcher}
          metricsStatus={accountMetricsStatus}
          expanded={overviewOpen}
          onToggle={toggleOverview}
        >
          <ControlsCategoriesView
            controls={[]}
            onSectionSelect={handleSectionSelect}
            selectedSection={selectedSection}
            sectionControls={complianceControls}
            isLoadingControls={false}
            controlsError={null}
            onResourceClick={handleResourceClick}
            compliance={compliance}
            auditRoster={auditRoster}
            variant="audit"
            embedded
          />
        </AuditAccountCard>
      ) : !compliance || !runId ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="50vh" width="100%">
          <Typography variant="h6" color="text.secondary" sx={{ textAlign: 'center', px: 2 }}>
            Missing required parameters. Please select a compliance framework from the dashboard.
          </Typography>
        </Box>
      ) : isGettingComplianceControlSections ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="80vh" width="100%">
          <CircularProgress color="primary" />
        </Box>
      ) : getComplianceControlSectionsError ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="80vh" width="100%">
          <Box width="100%">
            <ErrorUI
              error={getComplianceControlSectionsError}
              onRetry={() => dispatch(getComplianceControlSections({ compliance, runId }))}
            />
          </Box>
        </Box>
      ) : !complianceControlSections ||
        !Array.isArray(complianceControlSections.controlsListJson) ||
        complianceControlSections.controlsListJson.length === 0 ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="80vh" width="100%">
          <Typography variant="h6" color="text.secondary">
            No controls data available
          </Typography>
        </Box>
      ) : (
        <>
          {auditId ? (
            <AuditAccountCard
              scope={auditScope}
              audit={auditState.audit}
              resources={{
                total: Number(complianceControlSections.totalResources) || 0,
                passed: Number(complianceControlSections.passedResources) || 0,
                failed: Number(complianceControlSections.failedResources) || 0,
              }}
              compliance={compliance}
              scanDate={scanDate}
              accountNumber={accountNumber}
              accountSwitcher={accountSwitcher}
              metricsStatus={accountMetricsStatus}
              expanded={overviewOpen}
              onToggle={toggleOverview}
            >
              <ControlsCategoriesView
                controls={complianceControlSections.controlsListJson}
                onSectionSelect={handleSectionSelect}
                selectedSection={selectedSection}
                sectionControls={complianceControls}
                isLoadingControls={isGettingComplianceControls}
                controlsError={getComplianceControlsError}
                onResourceClick={handleResourceClick}
                compliance={compliance}
                runId={runId}
                auditRoster={auditRoster}
                variant="audit"
                embedded
              />
            </AuditAccountCard>
          ) : (
            <>
              <ControlsHeader
                controls={{
                  controlsListJson: complianceControlSections.controlsListJson,
                  trendScore: complianceControlSections.trendScore,
                  totalResources: complianceControlSections.totalResources,
                  passedResources: complianceControlSections.passedResources,
                  failedResources: complianceControlSections.failedResources,
                }}
                compliance={compliance}
                scanDate={scanDate}
                compliancePercent={compliancePercent}
                runId={runId}
                accountNumber={accountNumber}
              />
              <Box sx={{ 
                flex: 1, 
                display: "flex", 
                flexDirection: "column", 
                minHeight: 0,
                overflow: 'hidden', // Prevent overflow
              }}>
                <ControlsCategoriesView
                  controls={complianceControlSections.controlsListJson}
                  onSectionSelect={handleSectionSelect}
                  selectedSection={selectedSection}
                  sectionControls={complianceControls}
                  isLoadingControls={isGettingComplianceControls}
                  controlsError={getComplianceControlsError}
                  onResourceClick={handleResourceClick}
                  compliance={compliance}
                  runId={runId}
                  auditRoster={auditRoster}
                  variant="default"
                />
              </Box>
            </>
          )}
          <ControlResourcesDrawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            controlId={selectedControlId}
            resourcesUri={selectedResourcesUri}
            availableTypes={selectedAvailableTypes}
            availableRegions={selectedAvailableRegions}
            runId={selectedRunId}
          />
        </>
      )}
      </ScrollRegion>
    </Box>
    </AuditRefreshProvider>
    </ComplianceFontProvider>
  );
};

export default ControlsPage;
